import React, { useEffect, useState } from "react";
import type { Product } from "@/types";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { useConfig } from "@/lib/useConfig";
import { addToHomeScreen, canShareToChat, canShareToStory, haptic, homeScreenStatus, openExternalLink, shareToChat, shareToStory } from "@/lib/telegram";
import { fetchPriceWatch, fetchRecommendations, fetchStoryShare, unwatchPrice, watchPrice, type RecoProduct } from "@/lib/api";
import { ProductCard } from "./ProductCard";

/** "KMS on the home screen" — shown only where Telegram supports it and it isn't added yet. */
export function HomeScreenBanner() {
  const { t } = useI18n();
  const [show, setShow] = useState(false);
  useEffect(() => {
    let dismissed = false;
    try {
      dismissed = localStorage.getItem("kms.a2hsDismissed") === "1";
    } catch {
      /* ignore */
    }
    if (!dismissed) homeScreenStatus().then((s) => setShow(s === "missed"));
  }, []);
  if (!show) return null;
  const hide = () => {
    setShow(false);
    try {
      localStorage.setItem("kms.a2hsDismissed", "1");
    } catch {
      /* ignore */
    }
  };
  return (
    <div className="a2hs">
      <span className="a2hs__icon">
        <img src="/brand/kms-logo.png" alt="" />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <b>{t("growth.a2hsTitle")}</b>
        {t("growth.a2hsText")}
      </span>
      <button
        onClick={() => {
          haptic("light");
          addToHomeScreen();
          hide();
        }}
      >
        {t("growth.a2hsAdd")}
      </button>
      <button className="a2hs__close" onClick={hide} aria-label={t("common.close")}>
        ✕
      </button>
    </div>
  );
}

/** Lead capture entry. In exhibition mode it becomes a large banner with its own source tag. */
export function BusinessCta({ variant = "card" }: { variant?: "card" | "row" }) {
  const { t, locale } = useI18n();
  const { navigate } = useRouter();
  const { exhibition } = useConfig();
  if (exhibition) {
    return (
      <button className="expo-banner" onClick={() => navigate({ name: "lead", source: exhibition.source || "home_banner" })}>
        <span className="eyebrow" style={{ color: "rgba(255,255,255,.75)" }}>
          {exhibition.eyebrow?.[locale] || exhibition.eyebrow?.ru || t("lead.ctaEyebrow")}
        </span>
        <strong>{exhibition.title[locale] || exhibition.title.ru || t("lead.ctaTitle")}</strong>
        <span>{exhibition.text[locale] || exhibition.text.ru || t("lead.ctaText")}</span>
        <em>{t("lead.ctaButton")} →</em>
      </button>
    );
  }
  if (variant === "row") {
    return (
      <button className="card biz-row" onClick={() => navigate({ name: "lead", source: "profile" })}>
        <span>
          <b>{t("lead.ctaTitle")}</b>
          <small>{t("lead.ctaShort")}</small>
        </span>
        <span aria-hidden="true">›</span>
      </button>
    );
  }
  return (
    <button className="biz-card" onClick={() => navigate({ name: "lead", source: "home" })}>
      <span className="eyebrow">{t("lead.ctaEyebrow")}</span>
      <strong>{t("lead.ctaTitle")}</strong>
      <span>{t("lead.ctaText")}</span>
      <em>{t("lead.ctaButton")} →</em>
    </button>
  );
}

/** Share row on the product page: into any chat (inline mode), into a Telegram story, or copy the link. */
export function ShareRow({ product }: { product: Product }) {
  const { t } = useI18n();
  const { botUsername } = useConfig();
  const [copied, setCopied] = useState(false);
  const link = botUsername ? `https://t.me/${botUsername}?startapp=${`p_${product.slug}`.slice(0, 64)}` : null;
  const icon = (d: string) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" dangerouslySetInnerHTML={{ __html: d }} />
  );
  return (
    <div className="share-row2">
      {canShareToChat() && (
        <button onClick={() => { haptic("light"); shareToChat(product.name); }}>
          {icon('<path d="M4 5h16v11H8l-4 4z"/>')}
          {t("growth.shareChat")}
        </button>
      )}
      {canShareToStory() && (
        <button
          onClick={async () => {
            haptic("light");
            const r = await fetchStoryShare(product.slug).catch(() => null);
            if (r?.imageUrl) shareToStory(r.imageUrl, t("growth.storyText", { name: product.name }), r.link);
          }}
        >
          {icon('<circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="3.5"/>')}
          {t("growth.shareStory")}
        </button>
      )}
      {link && (
        <button
          onClick={async () => {
            haptic("light");
            try {
              await navigator.clipboard.writeText(link);
              setCopied(true);
              setTimeout(() => setCopied(false), 1600);
            } catch {
              openExternalLink(`https://t.me/share/url?url=${encodeURIComponent(link)}`);
            }
          }}
        >
          {icon('<path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1"/>')}
          {copied ? t("referral.copied") : t("growth.shareLink")}
        </button>
      )}
    </div>
  );
}

/** "Сообщить о скидке" toggle. */
export function PriceWatchToggle({ productId }: { productId: string }) {
  const { t } = useI18n();
  const [on, setOn] = useState<boolean | null>(null);
  useEffect(() => {
    fetchPriceWatch().then((ids) => setOn(ids.includes(productId))).catch(() => setOn(null));
  }, [productId]);
  if (on === null) return null;
  const toggle = async () => {
    haptic("selection");
    const next = !on;
    setOn(next);
    try {
      await (next ? watchPrice(productId) : unwatchPrice(productId));
    } catch {
      setOn(!next);
    }
  };
  return (
    <button className="pricewatch" onClick={toggle} role="switch" aria-checked={on}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M6 16V11a6 6 0 0 1 12 0v5l2 2H4zM10 20a2 2 0 0 0 4 0" />
      </svg>
      <span style={{ flex: 1, textAlign: "left" }}>
        <b>{t("growth.priceWatch")}</b>
        <small>{t("growth.priceWatchHint")}</small>
      </span>
      <span className="switch" data-on={on} aria-hidden="true" />
    </button>
  );
}

/** "Рекомендуем вам" rail with a short "why" under each card. */
export function RecommendationsRail() {
  const { t, locale } = useI18n();
  const [data, setData] = useState<{ personal: boolean; items: RecoProduct[] } | null>(null);
  useEffect(() => {
    fetchRecommendations().then(setData).catch(() => setData(null));
  }, [locale]);
  if (!data || data.items.length === 0) return null;
  const why = (p: RecoProduct) => (p.reason.ref ? t(`reco.${p.reason.code}`, { name: p.reason.ref }) : t(`reco.${p.reason.code}`));
  return (
    <>
      <div className="section-head">
        <div>
          <span className="eyebrow">{data.personal ? t("reco.eyebrowPersonal") : t("reco.eyebrowPopular")}</span>
          <h2>{t("reco.title")}</h2>
        </div>
      </div>
      <div className="rail">
        {data.items.map((p) => (
          <div key={p.id} style={{ width: 160, flexShrink: 0 }}>
            <ProductCard product={p} width="100%" />
            <div className="reco-why">{why(p)}</div>
          </div>
        ))}
      </div>
    </>
  );
}
